import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

/**
 * Sistema › Relatórios › Site (só GB Code): os leads que o brikcrm.com.br manda
 * para a entrada "Site" da empresa dona, e a própria conexão (etapa A); o que o
 * GA4 e o Search Console dizem do site (etapa B); e as contas criadas pelo site,
 * com por onde a pessoa chegou e se está usando (etapa C e tela única de 17/10).
 *
 * Propostas: docs/designsystem/propostas/admin/fase-7-site-tela-a.html (direção B,
 * §6 diz onde a etapa B mora) e fase-7-site.html (§3 os blocos, §8 as decisões).
 */

export interface SiteConexao {
    conectado: boolean;
    conexaoId: number | null;
    nome: string | null;
    /** O endereço do webhook de entrada. É a senha da entrada (T4: continua visível). */
    url: string | null;
    criadaEm: string | null;
    ultimoLeadEm: string | null;
    diasSemLead: number | null;
    /** O autoteste do formulário, feito na hora pela API. Pode faltar numa API mais antiga. */
    formulario?: SiteFormularioTeste | null;
}

/**
 * O site tem o endereço desta entrada? "ok" e "sem_teste" (site publicado antes da rota de
 * teste) não pedem nada; os outros três viram aviso na tela e no ntfy.
 */
export interface SiteFormularioTeste {
    situacao: 'ok' | 'sem_variavel' | 'endereco_errado' | 'site_fora' | 'sem_teste';
    verificadoEm: string;
}

export interface SiteLead {
    /** Única na lista: "f:{lead}" no formulário, "w:{conversa}" no WhatsApp. */
    chave: string;
    /** Por onde a pessoa chegou: o formulário do site ou o botão do WhatsApp. */
    canal: 'formulario' | 'whatsapp';
    /** O lead da entrada; 0 no WhatsApp, que não grava lead. */
    leadId: number;
    recebidoEm: string;
    nome: string | null;
    telefone: string | null;
    email: string | null;
    ferramenta: string | null;
    origem: string | null;
    veioDe: string | null;
    pagina: string | null;
    /** O resultado da ferramenta em português, escrito pelo site (T1). */
    resumo: string | null;
    /** O texto do formulário de contato. */
    mensagem: string | null;
    /** O objeto cru da ferramenta. Pode chegar como objeto ou como JSON em texto. */
    dados: unknown;
    utm: unknown;
    conversaId: number | null;
    etapaId: number | null;
    responsavel: string | null;
}

export interface SiteResumo {
    periodo: { de: string; ate: string };
    /** Formulário + WhatsApp, cada pessoa uma vez. */
    leads: number;
    leadsFormulario: number;
    leadsWhatsapp: number;
    leadsAnterior: number;
    emAberto: number;
    naPrimeiraEtapa: number;
    ganhos: number;
    perdidos: number;
    porDia: { dia: string; leads: number; formulario: number; whatsapp: number; cadastros: number }[];
    porFerramenta: { ferramenta: string; leads: number }[];
    lista: SiteLead[];
    /** Testes grátis que vieram do site. */
    cadastros: number;
    /** Todos os cadastros do período, de qualquer origem. */
    cadastrosTotal: number;
    cadastrosAnterior: number;
    cadastrosAssinantes: number;
    /** Dos testes do site no período, quantos estão usando (ver `SiteCadastro.usando`). */
    cadastrosUsando: number;
    /** A origem do cadastro só é gravada a partir desta data (yyyy-mm-dd). */
    origemDoCadastroDesde: string;
    listaCadastros: SiteCadastro[];
}

/** Uma empresa criada pelo teste grátis a partir de um link do site. */
export interface SiteCadastro {
    empresaId: number;
    empresa: string;
    criadoEm: string;
    admin: string | null;
    email: string | null;
    /** A página ou o bloco de onde saiu (utm_content): "home-hero", "segmentos/clinicas". */
    pagina: string | null;
    origem: string | null;
    /** Por onde a pessoa chegou ao site antes de clicar: "Google", "Instagram", "Direto" (desde 17/10). */
    fonte: string | null;
    /** A primeira página que ela abriu no site (desde 17/10). */
    entrada: string | null;
    /** O último login de alguém da conta, sem o modo suporte. */
    ultimaSessao: string | null;
    /** Voltou ao CRM depois do dia do cadastro e entrou nos últimos 7 dias. */
    usando: boolean;
    situacao: 'teste' | 'assinante' | 'vencido';
    assinaturaExpira: string | null;
}

/* ── etapa B: os dados do Google ───────────────────────────────── */

/** Qual site a aba Google mostra (D3): brikcrm.com.br por padrão, useBrik opcional. */
export type SiteChave = 'brikcrm' | 'usebrik';

/** O site respondeu? Verificado pela API a cada abertura, sem o cache diário. */
export interface SiteNoAr {
    ok: boolean;
    /** Código HTTP da resposta; `null` quando o site não respondeu. */
    status: number | null;
    tempoMs: number | null;
    verificadoEm: string;
    url: string;
}

export interface SiteBusca {
    consulta: string;
    cliques: number;
    impressoes: number;
    /** 0..1 */
    ctr: number;
    posicao: number;
}

export interface SiteSitemap {
    caminho: string;
    enviadoEm: string | null;
    lidoEm: string | null;
    enviadas: number;
    indexadas: number;
    erros: number;
    avisos: number;
}

export interface SiteGoogle {
    /** `false` quando a API não tem a chave da conta de leitura (Google:SiteLeitor). */
    configurado: boolean;
    site: string;
    /** Quando a API coletou os dados (a coleta é diária; "Atualizar agora" força). */
    atualizadoEm: string | null;
    /** Último dia que o Search Console já entregou (ele tem 2 a 3 dias de atraso). */
    buscasAte: string | null;
    visitas: number;
    visitasAnterior: number;
    usuarios: number;
    /** 0..1 — a taxa de engajamento do GA4. */
    engajamento: number;
    visitasGoogle: number;
    visitasIas: number;
    porDia: { dia: string; visitas: number }[];
    porOrigem: { origem: string; visitas: number }[];
    paginas: { pagina: string; visitas: number; engajamento: number }[];
    secoes: { secao: string; visitas: number }[];
    eventos: { evento: string; total: number }[];
    /** `null` quando o Search Console não foi liberado para o site (a useBrik, hoje). */
    buscas: SiteBusca[] | null;
    buscasTotais: { cliques: number; impressoes: number; ctr: number; posicaoMedia: number } | null;
    sitemaps: SiteSitemap[] | null;
    /** Pode faltar numa API mais antiga que o campo. */
    noAr?: SiteNoAr | null;
}

interface Envelope<T> { sucesso: boolean; mensagem: string; dados: T }

const ler = async <T>(resposta: Response): Promise<T> => {
    const corpo = await resposta.json().catch(() => null) as Envelope<T> | null;
    if (!resposta.ok || !corpo?.sucesso) {
        throw new Error(corpo?.mensagem || `Falha ao carregar (${resposta.status}).`);
    }
    return corpo.dados;
};

export const SiteSistemaService = {
    async conexao(): Promise<SiteConexao> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/site/conexao`);
        return ler<SiteConexao>(resposta);
    },

    /** Cria a entrada "Site brikcrm.com.br" na empresa dona e devolve a conexão já ligada. */
    async conectar(): Promise<SiteConexao> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/site/conectar`, { method: 'POST' });
        return ler<SiteConexao>(resposta);
    },

    async resumo(de: string, ate: string): Promise<SiteResumo> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/site/resumo?de=${de}&ate=${ate}`);
        return ler<SiteResumo>(resposta);
    },

    /** O que a API já coletou do GA4 e do Search Console (cache diário) + o "no ar" da hora. */
    async google(de: string, ate: string, site: SiteChave): Promise<SiteGoogle> {
        const resposta = await apiFetch(`${apiUrl}/api/sistema/site/google?de=${de}&ate=${ate}&site=${site}`);
        return ler<SiteGoogle>(resposta);
    },

    /** "Atualizar agora": força a coleta no Google e devolve os dados recém-coletados. */
    async atualizarGoogle(de: string, ate: string, site: SiteChave): Promise<SiteGoogle> {
        const resposta = await apiFetch(
            `${apiUrl}/api/sistema/site/google/atualizar?site=${site}&de=${de}&ate=${ate}`,
            { method: 'POST' },
        );
        return ler<SiteGoogle>(resposta);
    },
};
